/**
 * Acceptable Use Policy Page
 * Complete acceptable use policy for Rely platform
 */

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@chakra-ui/react';
import { MdPolicy } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import LegalPageLayout from '../../components/legal/LegalPageLayout';
import {
  LegalSection,
  LegalParagraph,
  LegalList,
  LegalDisclaimer,
  LegalSubSection,
  LegalContact,
  LegalDivider,
} from '../../components/legal/LegalSection';
import { LEGAL_CONFIG } from '../../config/legal';

const AUPPage = () => {
  const { t } = useTranslation();

  return (
    <LegalPageLayout
      title={t('aup.title', 'Política de Uso Aceptable')}
      subtitle={t(
        'aup.subtitle',
        'Normas de conducta para el uso de nuestros servicios'
      )}
      documentType="aup"
      icon={MdPolicy}
    >
      {/* Introduction */}
      <LegalParagraph>
        {t(
          'aup.intro',
          `Esta Política de Uso Aceptable ("PUA") establece las normas de conducta para el uso de los servicios de ${LEGAL_CONFIG.company.name}. Al utilizar nuestros servicios, usted acepta cumplir con esta política.`
        )}
      </LegalParagraph>

      {/* Section 1: Purpose */}
      <LegalSection
        number={1}
        title={t('aup.purpose.title', 'Propósito')}
        id="purpose"
      >
        <LegalParagraph>
          {t(
            'aup.purpose.description',
            'Rely proporciona servicios de formacion de empresas para emprendedores legitimos que desean establecer negocios legales en Estados Unidos. Estos servicios deben utilizarse unicamente para propositos legales y eticos.'
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 2: Permitted Uses */}
      <LegalSection
        number={2}
        title={t('aup.permitted.title', 'Usos Permitidos')}
        id="permitted"
      >
        <LegalParagraph>
          {t('aup.permitted.intro', 'Puede utilizar nuestros servicios para:')}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'aup.permitted.formation',
              'Formar LLCs para actividades comerciales legitimas'
            ),
            t('aup.permitted.investment', 'Inversiones y tenencia de activos'),
            t(
              'aup.permitted.consulting',
              'Consultoria y servicios profesionales'
            ),
            t(
              'aup.permitted.ecommerce',
              'Comercio electronico y ventas en linea'
            ),
            t('aup.permitted.real', 'Inversiones inmobiliarias'),
            t('aup.permitted.tech', 'Startups de tecnologia y software'),
            t('aup.permitted.freelance', 'Trabajo independiente y contratos'),
          ]}
        />
      </LegalSection>

      {/* Section 3: Prohibited Activities */}
      <LegalSection
        number={3}
        title={t('aup.prohibited.title', 'Actividades Prohibidas')}
        id="prohibited"
      >
        <LegalDisclaimer
          type="error"
          title={t('aup.prohibited.warning', 'Prohibido')}
        >
          {t(
            'aup.prohibited.warning.text',
            'Las siguientes actividades estan estrictamente prohibidas y resultaran en la terminacion inmediata del servicio y posibles acciones legales.'
          )}
        </LegalDisclaimer>

        <LegalSubSection
          title={t('aup.prohibited.illegal.title', 'Actividades Ilegales')}
        >
          <LegalList
            items={LEGAL_CONFIG.acceptableUse.prohibitedActivities.map(
              (activity, index) =>
                t(`aup.prohibited.illegal.${index}`, activity)
            )}
          />
        </LegalSubSection>

        <LegalSubSection
          title={t('aup.prohibited.content.title', 'Contenido Prohibido')}
        >
          <LegalList
            items={LEGAL_CONFIG.acceptableUse.contentRestrictions.map(
              (restriction, index) =>
                t(`aup.prohibited.content.${index}`, restriction)
            )}
          />
        </LegalSubSection>
      </LegalSection>

      {/* Section 4: Shell Companies */}
      <LegalSection
        number={4}
        title={t('aup.shell.title', 'Empresas de Fachada')}
        id="shell-companies"
      >
        <LegalParagraph>
          {t(
            'aup.shell.intro',
            'Esta prohibido utilizar nuestros servicios para crear empresas de fachada con el proposito de:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t('aup.shell.money', 'Lavado de dinero'),
            t('aup.shell.tax', 'Evasion fiscal'),
            t('aup.shell.sanctions', 'Evadir sanciones internacionales'),
            t(
              'aup.shell.identity',
              'Ocultar la identidad de beneficiarios finales para propositos ilegales'
            ),
            t('aup.shell.fraud', 'Fraude o estafas'),
          ]}
        />
        <LegalParagraph>
          {t(
            'aup.shell.kyc',
            'Nos reservamos el derecho de solicitar documentacion adicional para verificar la legitimidad de su negocio.'
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 5: Financial Services */}
      <LegalSection
        number={5}
        title={t('aup.financial.title', 'Servicios Financieros')}
        id="financial-services"
      >
        <LegalParagraph>
          {t(
            'aup.financial.description',
            'Si solicita servicios bancarios a traves de nuestros socios (Mercury, Relay), debe:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'aup.financial.truthful',
              'Proporcionar informacion veraz sobre su negocio'
            ),
            t(
              'aup.financial.compliance',
              'Cumplir con las regulaciones bancarias y AML'
            ),
            t(
              'aup.financial.use',
              'Usar la cuenta solo para actividades comerciales legitimas'
            ),
            t(
              'aup.financial.misuse',
              'No utilizar la cuenta para transacciones prohibidas'
            ),
          ]}
        />
      </LegalSection>

      {/* Section 6: Platform Security */}
      <LegalSection
        number={6}
        title={t('aup.security.title', 'Seguridad de la Plataforma')}
        id="security"
      >
        <LegalParagraph>
          {t(
            'aup.security.intro',
            'Para proteger nuestra plataforma y otros usuarios, esta prohibido:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'aup.security.hack',
              'Intentar acceder sin autorización a sistemas o datos'
            ),
            t(
              'aup.security.malware',
              'Introducir virus, malware u otro código dañino'
            ),
            t('aup.security.attack', 'Realizar ataques DDoS o de otro tipo'),
            t(
              'aup.security.scrape',
              'Scraping o recoleccion automatizada de datos'
            ),
            t(
              'aup.security.interfere',
              'Interferir con el funcionamiento normal de la plataforma'
            ),
            t(
              'aup.security.impersonate',
              'Suplantar la identidad de otros usuarios o de Rely'
            ),
          ]}
        />
      </LegalSection>

      {/* Section 7: Account Responsibilities */}
      <LegalSection
        number={7}
        title={t('aup.account.title', 'Responsabilidades de la Cuenta')}
        id="account"
      >
        <LegalParagraph>
          {t(
            'aup.account.intro',
            'Como titular de una cuenta, usted es responsable de:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'aup.account.credentials',
              'Mantener la seguridad de sus credenciales'
            ),
            t(
              'aup.account.actions',
              'Todas las acciones realizadas desde su cuenta'
            ),
            t('aup.account.update', 'Mantener su informacion actualizada'),
            t(
              'aup.account.notify',
              'Notificar inmediatamente de cualquier uso no autorizado'
            ),
          ]}
        />
      </LegalSection>

      {/* Section 8: Reporting Violations */}
      <LegalSection
        number={8}
        title={t('aup.reporting.title', 'Reportar Violaciones')}
        id="reporting"
      >
        <LegalParagraph>
          {t(
            'aup.reporting.description',
            `Si tiene conocimiento de alguna violacion de esta politica, por favor reportela a ${LEGAL_CONFIG.contact.support}. Investigaremos todos los reportes y tomaremos las medidas apropiadas.`
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 9: Consequences */}
      <LegalSection
        number={9}
        title={t('aup.consequences.title', 'Consecuencias de Violaciones')}
        id="consequences"
      >
        <LegalParagraph>
          {t(
            'aup.consequences.intro',
            'Las violaciones de esta politica pueden resultar en:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'aup.consequences.warning',
              'Advertencia por primera infraccion menor'
            ),
            t(
              'aup.consequences.suspension',
              'Suspension temporal de la cuenta'
            ),
            t(
              'aup.consequences.termination',
              'Terminacion permanente del servicio'
            ),
            t('aup.consequences.norefund', 'Perdida de reembolsos'),
            t(
              'aup.consequences.legal',
              'Acciones legales y denuncia a autoridades'
            ),
            t(
              'aup.consequences.liability',
              'Responsabilidad por danos causados'
            ),
          ]}
        />
        <LegalDisclaimer
          type="warning"
          title={t('aup.consequences.severe.title', 'Violaciones Graves')}
        >
          {t(
            'aup.consequences.severe.text',
            'Las violaciones relacionadas con actividades ilegales seran reportadas a las autoridades competentes y resultaran en terminacion inmediata sin reembolso.'
          )}
        </LegalDisclaimer>
      </LegalSection>

      {/* Section 10: Modifications */}
      <LegalSection
        number={10}
        title={t('aup.modifications.title', 'Modificaciones')}
        id="modifications"
      >
        <LegalParagraph>
          {t(
            'aup.modifications.text',
            'Nos reservamos el derecho de modificar esta Política de Uso Aceptable en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación. Su uso continuado de nuestros servicios constituye su aceptación de la política modificada.'
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 11: Related Documents */}
      <LegalSection
        number={11}
        title={t('aup.related.title', 'Documentos Relacionados')}
        id="related"
      >
        <LegalParagraph>
          {t('aup.related.intro', 'Esta política debe leerse junto con:')}
        </LegalParagraph>
        <LegalList
          items={[
            <Link as={RouterLink} to="/terms" color="brand.500" key="terms">
              {t('aup.related.terms', 'Términos de Servicio')}
            </Link>,
            <Link as={RouterLink} to="/privacy" color="brand.500" key="privacy">
              {t('aup.related.privacy', 'Política de Privacidad')}
            </Link>,
            <Link
              as={RouterLink}
              to="/refund-policy"
              color="brand.500"
              key="refund"
            >
              {t('aup.related.refund', 'Política de Reembolsos')}
            </Link>,
          ]}
        />
      </LegalSection>

      <LegalDivider />

      {/* Contact Information */}
      <LegalSection title={t('aup.contact.title', 'Contacto')} id="contact">
        <LegalParagraph>
          {t(
            'aup.contact.text',
            'Si tiene preguntas sobre esta politica o desea reportar una violacion:'
          )}
        </LegalParagraph>
        <LegalContact
          label={t('aup.contact.support', 'Soporte')}
          email={LEGAL_CONFIG.contact.support}
          description={t(
            'aup.contact.description',
            'Para preguntas o reportes'
          )}
        />
      </LegalSection>
    </LegalPageLayout>
  );
};

export default AUPPage;
